import React, { useCallback, useMemo } from 'react'

import { FormikProps, useFormikContext } from 'formik'
import {
  BoxV4,
  DatePickerV2,
  SelectV4,
  TextInputV4,
} from '@labrav/react-components'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { get, isEmpty } from 'lodash'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { ContractDurationOptions } from '../../constants'

import { useStyles } from './ContractDurationCombinedForm.styles'
import { SelectChangeEvent, Typography } from '@mui/material'
import {
  FieldMetadata,
  FieldMetadataField,
  ScheduleType,
} from '../../../../modules/privateOffer/types'
import { ResaleAuthorizationWithErrors } from '../../../../modules/resaleAuthorization/types'
import {
  labraUIDateFormatForm,
  addOneDayToDate,
  calculateDurationInMonths,
} from '../../helpers'
import { getAmiInstallmentsBasedOnSchedule } from '../../../../utils/amiPrivateOfferUtil'
import {
  awsMaxContractDurationMonths,
  awsMaxContractDurationYears,
} from '../../../../common/utils/constants'
import clsx from 'clsx'
import { useSelector } from '../../../../store'
import type { ContractDurationResellerProps } from '../../types'

export { ContractDurationOptions } from '../../constants'
export type { ContractDurationResellerProps }

export const ContractDurationReseller: React.FC<
  ContractDurationResellerProps
> = ({ disabled, isExternal, product }) => {
  const formikProps =
    useFormikContext() as FormikProps<ResaleAuthorizationWithErrors>
  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = formikProps
  const { classes } = useStyles()
  const minDate = useMemo(() => {
    if (values.agreementExpirationDate) {
      return addOneDayToDate(values.agreementExpirationDate as string)
    }

    return new Date().toISOString().split('T')[0]
  }, [values.agreementExpirationDate])

  const fieldMetadata = useSelector<FieldMetadata | undefined>(
    state =>
      state.resaleAuthorizationsInCreation.currentResaleAuthorizationInCreation[
        product?.productId as string
      ]?.fieldMetadata
  )
  const isContractDurationHidden = (
    fieldMetadata?.contractDurationInMonthsForEndCustomer as FieldMetadataField
  )?.hidden
  const isContractDurationReadOnly = (
    fieldMetadata?.contractDurationInMonthsForEndCustomer as FieldMetadataField
  )?.readOnly
  const isMaximumAllowedServiceDateHidden = (
    fieldMetadata?.maxAgreementStartDate as FieldMetadataField
  )?.hidden
  const isMaximumAllowedServiceDateReadOnly = (
    fieldMetadata?.maxAgreementStartDate as FieldMetadataField
  )?.readOnly

  const regenerateInstallments = useCallback(
    (containerOfferServiceLength: string | number, unitTime: string) => {
      const durationInMonths = calculateDurationInMonths(
        containerOfferServiceLength,
        unitTime
      )
      const paymentSchedule =
        values?.flexiblePaymentType || ScheduleType.MONTHLY
      const offerStartDate = minDate
      const tcv = values?.tcv

      // Only regenerate if not CUSTOM and we have required values
      if (paymentSchedule !== ScheduleType.CUSTOM && durationInMonths && tcv) {
        const newInstallments = getAmiInstallmentsBasedOnSchedule({
          tcv: tcv || 0,
          durationInMonths: durationInMonths || 0,
          paymentSchedule: paymentSchedule as ScheduleType,
          now: offerStartDate as string,
        })

        // Set first installment paymentDate from firstInstallmentDate if available (for AMI)
        if (values?.firstInstallmentDate && newInstallments.length > 0) {
          newInstallments[0].paymentDate = values?.firstInstallmentDate
        }

        setFieldValue('installmentInfo', newInstallments)
      }
    },
    [
      values?.flexiblePaymentType,
      values?.offerStartDate,
      values?.firstInstallmentDate,
      values?.tcv,
      setFieldValue,
      values.contractDurationInMonthsForEndCustomer,
      values.contractDurationTimeUnit,
      minDate,
    ]
  )
  const handleChangeContractDuration = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target
    const numericValue = value === '' ? '' : Number(value)
    setFieldTouched('contractDurationInMonthsForEndCustomer', true)
    await setFieldValue('contractDurationInMonthsForEndCustomer', numericValue)
    regenerateInstallments(
      numericValue,
      values.contractDurationTimeUnit as string
    )
  }
  const maximumAllowedServiceDateField = {
    title: 'Maximum allowed service start date (Optional)',
    humanReadableKey: 'Maximum allowed service start date (Optional)',
    placeHolder: 'Maximum allowed service start date (Optional)',
    id: 'maxAgreementStartDate',
    isRequired: false,
    isReadOnly: isMaximumAllowedServiceDateReadOnly || false,
    isCustom: false,
    dataType: 'date',
    minDate: 'today',
  }

  const ContractDurationSelectField = {
    title: 'Duration type',
    placeHolder: 'Duration type',
    humanReadableKey: 'Duration type',
    id: 'contractDurationTimeUnit',
    isRequired: false,
    isReadOnly: disabled,
    isCustom: false,
    defaultValue: 'Duration type',
    dataType: 'enum' as DataType,
  }

  const handleChangeSelect = useCallback(
    (event: SelectChangeEvent) => {
      const selectedValues = event.target.value as string
      setFieldValue('contractDurationTimeUnit', selectedValues)
      setFieldTouched('contractDurationInMonthsForEndCustomer', false)
      setFieldValue('contractDurationInMonthsForEndCustomer', '')
      regenerateInstallments(
        values.contractDurationInMonthsForEndCustomer as number,
        values.contractDurationTimeUnit as string
      )
    },
    [values.contractDurationTimeUnit]
  )

  const durationText = useMemo(() => {
    return values.contractDurationTimeUnit === 'MONTHS'
      ? 'Duration (in months)'
      : 'Duration (in years)'
  }, [values.contractDurationTimeUnit])

  return (
    <div data-testid="Contract-duration-section-combined-form">
      <BoxV4
        heading={'Contract duration'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
        // secondaryActions={
        //   <HelpDrawer
        //     sectionName="combinedFormContractDuration"
        //     headerTitle="Help & FAQs"
        //   />
        // }
      >
        {!isContractDurationHidden && (
          <>
            <div className={classes.sectionContainer}>
              <Typography className={classes.sectionHeading}>
                Specify the length of the contract. The price will switch back
                to public price after this duration.
              </Typography>
            </div>
            <div className={classes.sectionContainer}>
              <>
                <div className={classes.companyDetailsContainer}>
                  <div className={classes.inputWidth}>
                    <SelectV4
                      showInsideInfoIcon={true}
                      showInsideInfoIconTooltip={{
                        title: '',
                        content:
                          'Select the time unit for the contract duration.',
                      }}
                      field={{
                        ...ContractDurationSelectField,
                        options: ContractDurationOptions,
                      }}
                      value={values.contractDurationTimeUnit}
                      onChange={e => handleChangeSelect(e)}
                      onBlur={handleBlur}
                      isError={
                        !!(
                          get(touched, 'contractDurationTimeUnit', false) &&
                          get(errors, 'contractDurationTimeUnit', false)
                        )
                      }
                      errorText={
                        (!!get(touched, 'contractDurationTimeUnit', false) &&
                          get(errors, 'contractDurationTimeUnit', '')) ||
                        ''
                      }
                    />
                  </div>

                  {
                    <div className={classes.inputWidth}>
                      <TextInputV4
                        keyName="contractDurationInMonthsForEndCustomer"
                        title={durationText}
                        type="number"
                        placeHolder={durationText}
                        value={
                          values.contractDurationInMonthsForEndCustomer as number
                        }
                        updatedValue={
                          values.contractDurationInMonthsForEndCustomer as number
                        }
                        maxNumber={
                          values.contractDurationTimeUnit === 'YEARS'
                            ? awsMaxContractDurationYears
                            : awsMaxContractDurationMonths
                        }
                        isError={
                          !!(
                            get(
                              touched,
                              'contractDurationInMonthsForEndCustomer',
                              false
                            ) &&
                            get(
                              errors,
                              'contractDurationInMonthsForEndCustomer',
                              false
                            )
                          )
                        }
                        onChange={handleChangeContractDuration}
                        isReadOnly={
                          isContractDurationReadOnly || (disabled as boolean)
                        }
                        isRequired={false}
                        errorText={
                          (!!get(
                            touched,
                            'contractDurationInMonthsForEndCustomer',
                            false
                          ) &&
                            get(
                              errors,
                              'contractDurationInMonthsForEndCustomer',
                              ' '
                            )) ||
                          ' '
                        }
                        showSpinner={false}
                        onBlur={handleBlur}
                        keyToPrevent={['+', '-', 'e']}
                      />
                    </div>
                  }
                </div>
              </>
            </div>
          </>
        )}
        <div
          style={{
            display: 'flex',
            gap: '24px',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {!isMaximumAllowedServiceDateHidden && (
            <div
              className={clsx(
                isExternal ? classes.fullWidth : classes.inputWidth
              )}
            >
              <DatePickerV2
                format={labraUIDateFormatForm}
                views={['year', 'month', 'day']}
                field={maximumAllowedServiceDateField}
                key={'maxAgreementStartDate'}
                showErrorHelper={
                  !!touched.maxAgreementStartDate &&
                  !isEmpty(errors?.maxAgreementStartDate)
                }
                fieldId={'maxAgreementStartDate'}
                formik={
                  formikProps as unknown as FormikProps<Record<string, unknown>>
                }
                errorHelperText={
                  (!!touched?.maxAgreementStartDate &&
                    errors?.maxAgreementStartDate) ||
                  ''
                }
                size="small"
                toolTip={{
                  title: '',
                  content:
                    'Contract start date for future dates agreements cannot exceed this  date.',
                }}
              />
            </div>
          )}
          {!isExternal && <div className={classes.inputWidth}></div>}
        </div>
      </BoxV4>
    </div>
  )
}
